import type {
  Dispatch,
  MouseEventHandler,
  ReactNode,
  SetStateAction
} from 'react';
import { useEffect, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import type { ID, OptionItem } from 'common/types';
import { Pagination } from 'components/paginations';
import { Loader } from 'components/loader';
import { useSearchParams } from 'react-router-dom';
import { PAGE_SIZE } from 'common/constants';
import { Filter } from 'components/filter';
import { SortSvg } from 'components/svg/sort-svg';
import { CheckBox } from '../checkbox';
import {
  Cell,
  Row,
  Sort,
  SubTitle,
  TableActions,
  TableActionsCount,
  TableActionsItem,
  TableActionsWrapper,
  TableStyled,
  TableWrapper,
  TBody,
  Th,
  ThContainer,
  THead
} from './style';
import { defaultStyle, duration, transitionStyles } from './transition';

export interface HeaderItem<T> {
  id: keyof T;
  title: ReactNode;
  width?: string;
  maxWidth?: string;
  subtitle?: string;
  editable?: boolean;
  count?: number;
  filter?: {
    fetcher: (
      prams: string
    ) => Promise<{ options: OptionItem[]; count: number }>;
    searchKey?: string;
    filterByID?: boolean;
    limitKey?: string;
  };
  sortBy?: string;
  booleanFilter?: string;
  nowrap?: boolean;
}

interface TableProps<T> {
  headers: HeaderItem<T>[];
  data: T[];
  checkedList: number[];
  setCheckedList: Dispatch<React.SetStateAction<number[]>>;
  customHeight?: number;
  actions: {
    title: string;
    handler?: () => void;
  }[];
  count?: number;
  mt?: string;
  withOutCheck?: boolean;
  isFetching?: boolean;
  stickyColumns?: number;
  filters?: Record<string, string | boolean>;
  setFilters?: Dispatch<SetStateAction<Record<string, string | boolean>>>;
  withConfirmBtn?: boolean;
  setSortBy?: Dispatch<SetStateAction<string>>;
  sortBy?: string;
}

export const DataTable = <
  T extends {
    id: ID;
    to_response?: boolean;
    isHeader?: boolean;
  }
>({
  data,
  checkedList,
  setCheckedList,
  headers,
  customHeight,
  actions,
  mt,
  withOutCheck,
  isFetching,
  stickyColumns,
  count,
  filters,
  setFilters,
  withConfirmBtn,
  setSortBy,
  sortBy
}: TableProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const isCheckedAll = data.every((item) => checkedList.includes(+item.id));
  const [tableScrolled, setTableScrolled] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = searchParams.get('page');

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo(0, 0);
    }
  }, [currentPage]);

  const handleChackAll = (checked: boolean) => {
    if (checked) {
      setCheckedList((state) => {
        const updatedList = Array.from(
          new Set([...data.map((item) => +item.id), ...state])
        );

        return updatedList;
      });
    } else {
      setCheckedList((state) =>
        state.filter((item) => !data.map((item) => item.id).includes(item))
      );
    }
  };

  const handleChecked = (checked: boolean, id: number) => {
    if (checked) {
      setCheckedList((state) => [...state, id]);
    } else {
      setCheckedList((state) => state.filter((item) => item !== id));
    }
  };

  const handleClickCheckbox: MouseEventHandler<HTMLTableCellElement> = (e) => {
    e.stopPropagation();
  };

  const handleTableScroll = () => {
    if (stickyColumns && ref.current && ref.current.scrollLeft > 10) {
      setTableScrolled(true);
    } else {
      setTableScrolled(false);
    }
  };

  return (
    <TableWrapper
      ref={ref}
      mt={mt}
      style={{
        height: customHeight ? `${customHeight}px` : 'calc(100% - 282px)'
      }}
      onScroll={handleTableScroll}
    >
      {isFetching && (
        <Loader isBig place={{ left: 'calc(50% - 16px)', top: '100px' }} />
      )}
      <Transition
        in={!!checkedList.length}
        timeout={duration}
        mountOnEnter
        unmountOnExit
      >
        {(state) => (
          <TableActionsWrapper
            style={{
              ...defaultStyle,
              ...transitionStyles[state]
            }}
          >
            <TableActions>
              <TableActionsCount>
                {checkedList.length} обрано{' '}
              </TableActionsCount>
              {actions.map(({ title, handler }, i) => (
                <TableActionsItem onClick={handler} key={i}>
                  {title}
                </TableActionsItem>
              ))}
            </TableActions>
          </TableActionsWrapper>
        )}
      </Transition>
      <TableStyled $isFetching={isFetching}>
        <THead>
          <Row>
            {!withOutCheck && (
              <Th
                onClick={handleClickCheckbox}
                $showActions={!!checkedList.length}
              >
                <CheckBox checked={isCheckedAll} setChecked={handleChackAll} />
              </Th>
            )}
            {headers.map((item) => (
              <Th
                $stickyColumns={stickyColumns}
                $minWidth={
                  item.id === 'description' || item.id === 'categories'
                    ? '300px'
                    : 'unset'
                }
                $showActions={!!checkedList.length}
                key={item.id as string}
                $isEmpty={!item.title}
                style={item.width ? { minWidth: item.width } : undefined}
                $maxWidth={item.maxWidth}
                $nowrap={item.nowrap}
              >
                <ThContainer>
                  {!!item.sortBy && !!setSortBy && (
                    <Sort
                      onClick={() =>
                        setSortBy((state) =>
                          !state ||
                          (!!sortBy &&
                            item.sortBy &&
                            sortBy.indexOf(item.sortBy) === -1)
                            ? `${item.sortBy}_desc`
                            : state.endsWith('_asc')
                            ? ''
                            : `${item.sortBy}_asc`
                        )
                      }
                    >
                      <SortSvg
                        isAsc={
                          !!sortBy &&
                          sortBy.indexOf(item.sortBy) > -1 &&
                          !!sortBy.endsWith('_asc')
                        }
                        isDesc={
                          !!sortBy &&
                          sortBy.indexOf(item.sortBy) > -1 &&
                          !!sortBy.endsWith('_desc')
                        }
                      />
                    </Sort>
                  )}
                  <div>
                    {item.title}
                    {item.subtitle && <SubTitle>{item.subtitle}</SubTitle>}
                  </div>
                  {!!item.filter && filters && setFilters && (
                    <Filter
                      filters={filters}
                      setFilters={setFilters}
                      fieldKey={item.id as string}
                      {...item.filter}
                    />
                  )}
                  {typeof item.booleanFilter === 'string' &&
                    filters &&
                    setFilters && (
                      <CheckBox
                        checked={!!filters[item.booleanFilter]}
                        setChecked={(checked) => {
                          setFilters((state) => ({
                            ...state,
                            [item.booleanFilter as string]: checked
                          }));
                          searchParams.set('page', '1');
                          setSearchParams(searchParams);
                        }}
                      />
                    )}
                </ThContainer>
              </Th>
            ))}
          </Row>
        </THead>
        <TBody>
          {data.map((field) => (
            <Row key={field.id} checked={checkedList.includes(+field.id)}>
              {!withOutCheck && (
                <Cell onClick={handleClickCheckbox}>
                  <CheckBox
                    checked={checkedList.includes(+field.id)}
                    setChecked={(checked) => handleChecked(checked, +field.id)}
                  />
                </Cell>
              )}
              {headers.map((item) => (
                <Cell
                  $isGeneratePassword={item.id === 'generatePassword'}
                  $isEditable={
                    (!field.isHeader && item.editable) || item.id === 'print'
                  }
                  $stickyColumns={stickyColumns}
                  $tableScrolled={tableScrolled}
                  key={item.id as string}
                >
                  {field[item.id] as ReactNode}
                </Cell>
              ))}
            </Row>
          ))}
        </TBody>
      </TableStyled>
      {count && count > PAGE_SIZE ? (
        <Pagination withConfirmBtn={withConfirmBtn} count={count || 0} />
      ) : (
        ''
      )}
    </TableWrapper>
  );
};
